<script lang="ts">
  import { authState } from '../../lib/auth.svelte';
  import { db } from '../../lib/firebase';
  import { openNangoConnect, disconnectIntegration } from '../../lib/nango';
  import { collection, onSnapshot, doc, setDoc, updateDoc, deleteDoc, getDoc } from 'firebase/firestore';
  import type { Integration, ConnectedIntegration, IntegrationConfig, Business } from '../../lib/types';
  import LoadingSpinner from '../ui/LoadingSpinner.svelte';

  // Sub-components
  import IntegrationCard from './IntegrationCard.svelte';
  import IntegrationConfigModal from './IntegrationConfigModal.svelte';
  import SyncHistorySection from './SyncHistorySection.svelte';
  import ApiKeysSection from './ApiKeysSection.svelte';
  import WebhooksSection from './WebhooksSection.svelte';

  // Available integrations
  const availableIntegrations: Integration[] = [
    { id: 'shopify', name: 'Shopify', description: 'Import orders and sync inventory with Shopify', icon: 'S', enabled: true },
    { id: 'xero', name: 'Xero', description: 'Sync invoices and contacts with Xero accounting', icon: 'X', enabled: false },
    { id: 'woocommerce', name: 'WooCommerce', description: 'Connect your WooCommerce store for order sync', icon: 'W', enabled: false },
    { id: 'zapier', name: 'Zapier', description: 'Connect to 5,000+ apps via Zapier automations', icon: 'Z', enabled: false },
  ];

  // State
  let businesses = $state<Business[]>([]);
  let selectedBusinessId = $state<string | null>(null);
  let loadingBusiness = $state(true);
  let connectedIntegrations = $state<Map<string, ConnectedIntegration>>(new Map());
  let connecting = $state<string | null>(null);
  let error = $state<string | null>(null);

  // Config modal state
  let showConfigModal = $state(false);
  let configProviderId = $state<string | null>(null);

  // Sync history state
  let showSyncHistory = $state(false);
  let historyProviderId = $state<string | null>(null);

  let selectedBusinessName = $derived(
    businesses.find(b => b.id === selectedBusinessId)?.name ?? null
  );

  // Load businesses on mount
  $effect(() => {
    if (!authState.user) {
      loadingBusiness = false;
      return;
    }

    const loadBusinesses = async () => {
      try {
        const userDoc = await getDoc(doc(db, 'users', authState.user!.uid));
        if (userDoc.exists()) {
          const data = userDoc.data();
          const activeBusinesses: Business[] = (data.businesses ?? []).filter(
            (b: Business) => b.isActive !== false && b.isArchived !== true
          );
          businesses = activeBusinesses;

          // Default to selectedBusinessId from profile, or first business
          const defaultId = data.selectedBusinessId || activeBusinesses[0]?.id;
          selectedBusinessId = defaultId || null;
        }
      } catch (err) {
        console.error('Error loading businesses:', err);
        error = 'Failed to load business information';
      } finally {
        loadingBusiness = false;
      }
    };

    loadBusinesses();
  });

  function handleBusinessChange(e: Event) {
    const select = e.target as HTMLSelectElement;
    selectedBusinessId = select.value || null;
    // Reset connection state when switching business
    connectedIntegrations = new Map();
    connecting = null;
    error = null;
  }

  // Listen to integrations collection
  $effect(() => {
    if (!selectedBusinessId) return;

    const unsubscribe = onSnapshot(
      collection(db, 'businessdata', selectedBusinessId, 'integrations'),
      (snapshot) => {
        const newMap = new Map<string, ConnectedIntegration>();
        snapshot.forEach((doc) => {
          newMap.set(doc.id, doc.data() as ConnectedIntegration);
        });
        connectedIntegrations = newMap;
      },
      (err) => {
        console.error('Error listening to integrations:', err);
        error = 'Failed to load integrations';
      }
    );

    return () => unsubscribe();
  });

  async function handleConnect(providerId: string) {
    if (!selectedBusinessId || !authState.user) return;

    connecting = providerId;
    error = null;

    try {
      const result = await openNangoConnect(providerId, selectedBusinessId);

      await setDoc(doc(db, 'businessdata', selectedBusinessId, 'integrations', providerId), {
        provider: providerId,
        nangoConnectionId: result.connectionId,
        status: 'connected',
        connectedAt: new Date().toISOString(),
        connectedBy: authState.user.uid,
        lastSyncAt: null,
        lastSyncStatus: null,
        config: {
          syncOrders: true,
          syncInventory: true,
          orderSyncDirection: 'inbound',
          inventorySyncDirection: 'bidirectional',
        },
        error: null,
      });

      connecting = null;
    } catch (err: any) {
      connecting = null;
      if (err.message !== 'cancelled by user') {
        console.error('Error connecting integration:', err);
        error = `Failed to connect ${providerId}: ${err.message}`;
      }
    }
  }

  async function handleDisconnect(providerId: string) {
    if (!selectedBusinessId) return;

    const confirmed = window.confirm(
      `Are you sure you want to disconnect ${providerId}? This will stop all syncing with this integration.`
    );
    if (!confirmed) return;

    try {
      await disconnectIntegration(providerId, selectedBusinessId);
      await deleteDoc(doc(db, 'businessdata', selectedBusinessId, 'integrations', providerId));
    } catch (err: any) {
      console.error('Error disconnecting integration:', err);
      error = `Failed to disconnect ${providerId}: ${err.message}`;
    }
  }

  function handleConfigure(providerId: string) {
    configProviderId = providerId;
    showConfigModal = true;
  }

  function handleViewHistory(providerId: string) {
    historyProviderId = providerId;
    showSyncHistory = true;
  }

  async function handleSaveConfig(providerId: string, config: IntegrationConfig) {
    if (!selectedBusinessId) return;
    try {
      await updateDoc(
        doc(db, 'businessdata', selectedBusinessId, 'integrations', providerId),
        { config }
      );
      showConfigModal = false;
      configProviderId = null;
    } catch (err: any) {
      console.error('Error saving config:', err);
      error = `Failed to save config: ${err.message}`;
    }
  }
</script>

<div class="integrations-container">
  {#if loadingBusiness}
    <div class="loading-state">
      <LoadingSpinner />
      <p>Loading integrations...</p>
    </div>
  {:else}
    {#if businesses.length > 1}
      <div class="business-selector">
        <label for="business-select">Business</label>
        <select id="business-select" value={selectedBusinessId} onchange={handleBusinessChange}>
          {#each businesses as biz}
            <option value={biz.id}>{biz.name}</option>
          {/each}
        </select>
      </div>
    {:else if selectedBusinessName}
      <div class="business-context">
        Integrations for <strong>{selectedBusinessName}</strong>
      </div>
    {/if}

    {#if error}
      <div class="error-banner">
        <div class="error-content">
          <svg viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
          </svg>
          <span>{error}</span>
        </div>
        <button class="error-dismiss" onclick={() => error = null}>
          <svg viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
          </svg>
        </button>
      </div>
    {/if}

    <!-- API Keys Section -->
    <ApiKeysSection businessId={selectedBusinessId} />

    <!-- Webhooks Section -->
    <WebhooksSection businessId={selectedBusinessId} />

    <!-- Third-Party Integrations Section -->
    <section class="section">
      <div class="section-header">
        <div>
          <h2>Integrations</h2>
          <p class="section-description">Connect EQUOS with your favorite tools and services.</p>
        </div>
      </div>

      <div class="integrations-grid">
        {#each availableIntegrations as integration}
          <IntegrationCard
            {integration}
            connectionData={connectedIntegrations.get(integration.id) ?? null}
            isConnecting={connecting === integration.id}
            onConnect={handleConnect}
            onDisconnect={handleDisconnect}
            onConfigure={handleConfigure}
            onViewHistory={handleViewHistory}
          />
        {/each}
      </div>
    </section>
  {/if}
</div>

{#if showConfigModal && configProviderId}
  <IntegrationConfigModal
    providerId={configProviderId}
    config={connectedIntegrations.get(configProviderId)?.config ?? null}
    providerName={availableIntegrations.find(i => i.id === configProviderId)?.name ?? ''}
    onClose={() => { showConfigModal = false; configProviderId = null; }}
    onSave={handleSaveConfig}
  />
{/if}

{#if showSyncHistory && historyProviderId}
  <SyncHistorySection
    businessId={selectedBusinessId}
    provider={historyProviderId}
    providerName={availableIntegrations.find(i => i.id === historyProviderId)?.name ?? ''}
    onClose={() => { showSyncHistory = false; historyProviderId = null; }}
  />
{/if}

<style>
  .integrations-container {
    max-width: 900px;
  }

  .loading-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 3rem 2rem;
    color: var(--color-gray-500, #6b7280);
    gap: 1rem;
  }

  .business-selector {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin-bottom: 1.25rem;
    padding: 0.75rem 1rem;
    background: white;
    border-radius: var(--radius-md, 8px);
    border: 1px solid var(--color-border, #e5e7eb);
  }

  .business-selector label {
    font-size: 0.85rem;
    font-weight: 500;
    color: var(--color-gray-600, #4b5563);
    white-space: nowrap;
  }

  .business-selector select {
    flex: 1;
    padding: 0.5rem 0.75rem;
    border: 1px solid var(--color-border, #e5e7eb);
    border-radius: var(--radius-sm, 6px);
    font-size: 0.9rem;
    font-family: inherit;
    background: white;
    color: var(--color-text, #333);
    transition: border-color 0.2s;
  }

  .business-selector select:focus {
    outline: none;
    border-color: #667eea;
  }

  .business-context {
    font-size: 0.85rem;
    color: var(--color-gray-500, #6b7280);
    margin-bottom: 1rem;
    padding: 0.5rem 0;
  }

  .error-banner {
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: var(--color-error-bg, #fef2f2);
    border: 1px solid var(--color-error-border, #fecaca);
    border-radius: var(--radius-md, 8px);
    padding: 0.875rem 1rem;
    margin-bottom: 1.5rem;
    color: var(--color-error-text, #991b1b);
  }

  .error-content {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    flex: 1;
  }

  .error-content svg {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
  }

  .error-dismiss {
    background: none;
    border: none;
    cursor: pointer;
    color: var(--color-error-text, #991b1b);
    padding: 0;
    width: 20px;
    height: 20px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .error-dismiss:hover {
    opacity: 0.7;
  }

  .error-dismiss svg {
    width: 16px;
    height: 16px;
  }

  .section {
    background: white;
    border-radius: var(--radius-xl, 12px);
    padding: 1.5rem;
    margin-bottom: 1.5rem;
    box-shadow: var(--shadow-sm, 0 1px 3px rgba(0, 0, 0, 0.1));
  }

  .section-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 1.5rem;
  }

  .section-header h2 {
    margin: 0 0 0.25rem;
    font-size: 1.25rem;
    color: var(--color-gray-800, #1f2937);
  }

  .section-description {
    margin: 0;
    color: var(--color-gray-500, #6b7280);
    font-size: 0.9rem;
  }

  .integrations-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 1rem;
  }
</style>
